import { StyleSheet, View, Text, ActivityIndicator } from 'react-native';
import { useState, useEffect } from 'react';
import { getUserDataByUserId, getUserContactInfoByUserId } from '../../services/users/Users';
import { getPendingApprovalBusinesses, approveBusiness, denyBusiness } from '../../services/businesses/Businesses';
import SmallButton from '../shared/SmallButton';
import ConfirmModal from '../ConfirmModal';

export default function ApprovalsScreen({ navigation }) {

  const [isLoading, setIsLoading] = useState(true);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { businessId, bar, action: 'approve' | 'deny' }

  useEffect(() => {
    (async () => {
      // admin-only screen, redirect away if the signed in user isn't an admin
      let savedUserId = window.localStorage.getItem('userId');
      if (!savedUserId) {
        navigation.navigate('HomeScreen');
        return;
      }

      const userData = await getUserDataByUserId(savedUserId);
      if (!userData || userData.role !== 'admin') {
        navigation.navigate('HomeScreen');
        return;
      }

      await loadPendingBusinesses();
    })();
  }, [])

  const loadPendingBusinesses = async () => {
    setIsLoading(true);

    const businesses = await getPendingApprovalBusinesses();

    const businessesWithContactInfo = await Promise.all(
      businesses.map(async (business) => {
        const contactInfo = business.userId ? await getUserContactInfoByUserId(business.userId) : null;
        return {
          ...business,
          email: contactInfo?.email ? contactInfo.email : '',
          phone: contactInfo?.phone ? contactInfo.phone : '',
        };
      })
    );

    setPendingBusinesses(businessesWithContactInfo);
    setIsLoading(false);
  }

  const handleApproveClick = (business) => {
    setPendingAction({ businessId: business.id, bar: business.bar, action: 'approve' });
    setIsConfirmModalVisible(true);
  }

  const handleDenyClick = (business) => {
    setPendingAction({ businessId: business.id, bar: business.bar, action: 'deny' });
    setIsConfirmModalVisible(true);
  }

  const closeConfirmModal = () => {
    setIsConfirmModalVisible(false);
    setPendingAction(null);
  }

  const confirmAction = async () => {
    if (!pendingAction) {
      return;
    }

    if (pendingAction.action === 'approve') {
      await approveBusiness(pendingAction.businessId);
    } else {
      await denyBusiness(pendingAction.businessId);
    }

    setPendingBusinesses((current) => current.filter((business) => business.id !== pendingAction.businessId));
    setIsConfirmModalVisible(false);
    setPendingAction(null);
  }

  const confirmMessage = pendingAction
    ? `Are you sure you want to ${pendingAction.action} ${pendingAction.bar}?`
    : '';

  return (
    <View style={styles.container}>
      <Text style={styles.title}>User Business Pending Approval</Text>

      <ActivityIndicator size="large" color="#674FE0" animating={isLoading} hidesWhenStopped={true} />

      {!isLoading && pendingBusinesses.length === 0 &&
        <Text style={styles.emptyText}>No businesses are pending approval.</Text>
      }

      {!isLoading && pendingBusinesses.map((business) => (
        <View key={business.id} style={styles.businessCard}>
          <Text style={styles.barName}>{business.bar}</Text>
          <Text style={styles.contactInfo}>{business.phone ? business.phone : 'No phone on file'}</Text>
          <Text style={styles.contactInfo}>{business.email ? business.email : 'No email on file'}</Text>
          <View style={styles.buttonRow}>
            <SmallButton theme="basic" label="Approve" onPress={() => handleApproveClick(business)} />
            <SmallButton theme="secondary" label="Deny" onPress={() => handleDenyClick(business)} />
          </View>
        </View>
      ))}

      <ConfirmModal
        message={confirmMessage}
        visible={isConfirmModalVisible}
        dismiss={closeConfirmModal}
        confirmDelete={confirmAction}
      ></ConfirmModal>
    </View>
  );

}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F2F2F2',
    alignItems: 'center',
    paddingTop: 18,
    paddingHorizontal: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: '600',
    color: '#333',
    marginBottom: 14,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#555',
    marginTop: 20,
    textAlign: 'center',
  },
  businessCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  barName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  contactInfo: {
    fontSize: 14,
    color: '#555',
    marginBottom: 2,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: 10,
    gap: 10,
  },
});
